import helperFunction from "@/helper-functions/helperFunctions"
import i18n from "@/plugins/i18n"
import useCatchHandler from "@/services/catchErrorHandler"
import useOAuth from "@/services/oauth"
import { store, useStore } from "@/store"
import { ActionTypes as AuthActions } from "@/store/modules/auth/actions"
import axios from "axios"
import JSZip from "jszip"
import { computed } from "vue"
import { useRoute } from "vue-router"
import { toast } from "/src/plugins/vue3-toastify.plugin"
const getAiAssistToken = computed(() => store.getters.getAiAssistToken)
const PUBLIC_AUTH_HEADER = {
    headers: {
        Authorization: "6c45feb3-03cf-4a4b-a82b-3563ea8667b6",
    },
}
declare const saveAs: any

export default function useApiAssessment() {
    const { handleError } = useCatchHandler()
    const { API_PREFIX } = useOAuth()
    const route = useRoute()
    const zip = new JSZip()
    const { isStaging } = helperFunction()

    const store = useStore()
    const getApiRequestHeader = computed(
        () => store.getters.getApiRequestHeader
    )
    const aIWriteTokenValue = computed(() => store.getters.getAiAssistToken)

    const apiGetAssessmentLimit = async () => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/create/assessment/limit`,
                {
                    headers: {
                        ...getApiRequestHeader.value.headers,
                        Referrer: window.location.href,
                    },
                }
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetCompanyTestLimit = async () => {
        try {
            return await axios.get(
                `${API_PREFIX}/test/library/company/test/limit`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiCreateAssessment = async (
        title: string,
        jobRoleId: string,
        otherJobRole: string,
        isCloneTemplate: boolean,
        assessmentDescription: string,
        defaultLanguage: string,
        supportedLanguages: object,
        orgId: string = "",
        isInterview: boolean,
        interviewQuestionType: string = "",
        voiceType: string = "",
        aiGenerationMetadata: any = {},
        language: string = ""
    ) => {
        try {
            const body = {
                title,
                jobRoleId,
                otherJobRole,
                isCloneTemplate,
                assessmentDescription,
                defaultLanguage,
                supportedLanguages,
                orgId,
                isInterview,
                interviewQuestionType,
                voiceType,
                aiGenerationMetadata,
                language,
            }
            return await axios.post(
                `${API_PREFIX}/assessment`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            return response.data
        }
    }

    const apiAssessmentAnalytics = async (id: string, candidateId: string) => {
        try {
            if (route.query.hasOwnProperty("auth")) {
                const auth = route.query["auth"]
                let response: any = await axios.get(
                    `${API_PREFIX}/public/assessment/analytics?auth=${auth}`
                )
                return response
            } else {
                let url = `${API_PREFIX}/assessment/${id}/analytics`
                if (candidateId) {
                    url += `?candidateId=${candidateId}`
                }
                let response: any = await axios.get(
                    url,
                    getApiRequestHeader.value
                )
                return response
            }
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiAiAssessmentStatus = async (id: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/${id}/ai/status`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetAIAssitToken = async () => {
        try {
            const response = await axios.get(
                `${API_PREFIX}/workspace/ai/assist/token`,
                getApiRequestHeader.value
            )
            if (response) {
                //  set the token in store to use for calling AIWrite API
                store.dispatch(
                    AuthActions.SET_AI_WRITE_TOKEN,
                    response.data.aiAssistToken
                )
            }
        } catch ({ response }) {
            handleError(response)
        }
    }

    const apiAIWrite = async (action: string, text: string) => {
        let apiUrl

        if (isStaging) {
            apiUrl = `https://ai-assist-hnr.vercel.app/api/v1/ai/assist`
        } else {
            apiUrl = `https://ai-write.testlify.com/api/v1/ai/assist`
        }

        // check if getAiAssistToken has value
        if (!getAiAssistToken.value) {
            // if not present, call token API to populate getAiAssistToken
            await apiGetAIAssitToken()
            return await apiAIWrite(action, text)
        } else {
            // if present, call the below API
            try {
                const body = {
                    action,
                    text,
                }
                const response = await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${aIWriteTokenValue.value}`,
                    },
                    body: JSON.stringify(body),
                })
                if (!response.ok) {
                    // if request failed with token expired error(Token expires in 1 day)
                    // call token API to populate getAiAssistToken
                    await apiGetAIAssitToken()
                    return await apiAIWrite(action, text)
                }
                return response
            } catch (e) {
                return e
            }
        }
    }

    const apiCreateAssessmentGenerativeAI = async (
        title: string,
        jobRoleId: string,
        skills: string[],
        language: string,
        overview: string,
        experience: string,
        numberOfQuestions: number,
        assessmentId: "",
        regenerate: boolean,
        aiGenerationMetadata: any,
        questionTypes: any,
        files: string[] = []
    ) => {
        try {
            const body = {
                title,
                jobRoleId,
                numberOfQuestions,
                skills,
                language,
                experience,
                assessmentId,
                regenerate,
                aiGenerationMetadata,
                questionTypes,
                ...(overview !== "" && { overview }),
                files,
            }
            return await axios.post(
                `${API_PREFIX}/assessment/questions/ai/generate`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            return response.data
        }
    }

    const apiEditAssessment = async (
        id: string,
        title: string,
        jobRoleId: string,
        otherJobRole: string,
        libraries: object,
        customQuestions: object,
        assessmentDescription: string,
        aiGenerationMetadata: any,
        supportedLanguages: object,
        language: string = ""
    ) => {
        try {
            const body = {
                title,
                jobRoleId,
                otherJobRole,
                assessmentDescription,
                aiGenerationMetadata,
                libraries,
                customQuestions,
                supportedLanguages,
                language,
            }
            return await axios.patch(
                `${API_PREFIX}/assessment/${id}`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiDeleteAssessment = async (id: string) => {
        try {
            return await axios.delete(
                `${API_PREFIX}/assessment/${id}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiBulkDeleteAssessment = async (
        assessmentIds: string[],
        isAllSelect: boolean,
        query: string,
        isArchived: boolean,
        isEditable: boolean,
        isActive: boolean,
        isDraft: boolean,
        workspaceLabelTitle: Array<object>
    ) => {
        try {
            const body = { assessmentIds, isAllSelect }
            return await axios.delete(
                `${API_PREFIX}/assessment/bulk?query=${encodeURIComponent(
                    query
                )}&isArchived=${isArchived}&isEditable=${isEditable}&isActive=${isActive}&isDraft=${isDraft}&workspaceLabelTitle=${workspaceLabelTitle}`,
                {
                    data: body,
                    ...getApiRequestHeader.value,
                }
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiSessionRecordings = async (
        candidateEmails: string,
        assessmentIds: string,
        candidateIds: string
    ) => {
        const query = `?candidateEmails=${encodeURIComponent(
            candidateEmails
        )}&assessmentIds=${assessmentIds}&candidateIds=${candidateIds}`
        try {
            const endpoint = `${API_PREFIX}/assessment/recordings${query}`
            const { data } = await axios.get(
                endpoint,
                getApiRequestHeader.value
            )
            return data
        } catch ({ response }) {
            const errors: any = response!.data!.error
            const { statusCode } = errors
            if (statusCode === 400) {
                //bearer:disable javascript_lang_logger_leak
                console.log("statusCode", statusCode)
                return statusCode
            } else {
                handleError(response)
            }
            return -1
        }
    }

    const apiCloneAssessment = async (
        assessmentTitle: string,
        assessmentId: string,
        selectedWorkspaceList: string[]
    ) => {
        try {
            const body = {
                assessmentTitle,
                assessmentId,
                orgIds: selectedWorkspaceList,
            }
            return await axios.post(
                `${API_PREFIX}/assessment/clone`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiCloneCompanyTest = async (
        testLibraryTitle: string,
        testLibraryId: string
    ) => {
        try {
            const body = {
                testLibraryTitle,
                testLibraryId,
            }
            return await axios.post(
                `${API_PREFIX}/test/library/clone`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiEditArchive = async (
        id: string,
        title: string,
        isArchived: boolean,
        jobRoleId: string
    ) => {
        try {
            const body = { title, isArchived, jobRoleId }
            return await axios.patch(
                `${API_PREFIX}/assessment/${id}`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiArchiveAssessment = async (
        assessmentIds: string[],
        unarchive: boolean
    ) => {
        try {
            const body = { assessmentIds, unarchive }
            return await axios.patch(
                `${API_PREFIX}/assessment/archive`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetTestLangugesList = async () => {
        try {
            return await axios.get(
                `${API_PREFIX}/language`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetJobRoleList = async (query: string) => {
        try {
            const encodedQueryString = encodeURIComponent(query)
            return await axios.get(
                `${API_PREFIX}/workspace/jobrole?q=${encodedQueryString}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiFetchSkillsGenerativeAI = async (
        jobRoleId: string,
        skillCount: number = 10,
        query: string = ""
    ) => {
        try {
            return await axios.get(
                `${API_PREFIX}/workspace/jobrole/${jobRoleId}/${skillCount}?q=${query}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            if (response?.data?.error?.statusCode === 400) {
                toast.error(
                    `${i18n.global.t(
                        "create_assessment_ai.invalid_job_profile"
                    )}`
                )
            } else {
                handleError(response)
            }
            return -1
        }
    }

    const apiGetTemplates = async (
        limit: number,
        skip: number,
        query: string,
        template: boolean = true,
        type: string[],
        difficulty: string[],
        jobRoleId: string = "",
        sort: string,
        status: string,
        codingLanguage: string[],
        language: string[],
        archived: boolean,
        otherJobRole: string = "",
        isAiInterview: boolean = false,
        industryType: string[]
    ) => {
        try {
            return await axios.get(
                `${API_PREFIX}/test/library/template/search?limit=${limit}&skip=${skip}&query=${encodeURIComponent(
                    query
                )}&template=${template}&type=${type}&difficulty=${difficulty}&jobRoleId=${jobRoleId}&sort=${sort}&status=${status}&codingLanguage=${codingLanguage}&language=${language}&archived=${archived}&otherJobRole=${otherJobRole}&isAiInterview=${isAiInterview}&industryType=${industryType}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetTestList = async (
        limit: number,
        skip: number,
        query: string,
        type: string[],
        difficulty: string[],
        jobRoleId: string = "",
        sort: string,
        isCompanyTest: boolean,
        status: string,
        codingLanguage: string[],
        isTestlifyLibraries: boolean,
        language: string[],
        isAssessment: boolean,
        archived: boolean,
        industryType: string[],
        template: boolean = false,
        otherJobRole: string = "",
        isAiInterview: boolean = false
    ) => {
        try {
            return await axios.get(
                `${API_PREFIX}/test/library/search?limit=${limit}&skip=${skip}&query=${encodeURIComponent(
                    query
                )}&template=${template}&type=${type}&difficulty=${difficulty}&jobRoleId=${jobRoleId}&sort=${sort}&isCompanyTest=${isCompanyTest}&status=${status}&codingLanguage=${codingLanguage}&isTestlifyLibraries=${isTestlifyLibraries}&language=${language}&isAssessment=${isAssessment}&archived=${archived}&industryType=${industryType}&otherJobRole=${otherJobRole}&isAiInterview=${isAiInterview}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetTypeList = async () => {
        try {
            return await axios.get(
                `${API_PREFIX}/static/testlibrary/type`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetIndustryTypes = async () => {
        try {
            return await axios.get(
                `${API_PREFIX}/static/testlibrary/industry/types`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetSkillTypes = async (query: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/candidate/skills?skip=0&limit=50&query=${query}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetLocationTypes = async (query: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/static/master/location?limit=50&skip=0&query=${query}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetInterviewStatusCount = async () => {
        try {
            return await axios.get(
                `${API_PREFIX}/candidate/interview/count`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiAssessmentUsersAndRoles = async () => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/permissions`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiUpdateAssessment = async (id: string, body: object) => {
        try {
            return await axios.put(
                `${API_PREFIX}/assessment/${id}`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiUpdateTestLibrary = async (id: string, body: object) => {
        try {
            return await axios.put(
                `${API_PREFIX}/assessment/${id}/test-library`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiUpdateInstruction = async (id: string, body: object) => {
        try {
            return await axios.put(
                `${API_PREFIX}/assessment/question/update?testLibraryId=${id}`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiUpdateInstructionAssessment = async (id: string, body: object) => {
        try {
            return await axios.put(
                `${API_PREFIX}/assessment/question/update?assessmentId=${id}`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiSetTimeToCompleteQuestions = async (id: string, body: object) => {
        try {
            return await axios.put(
                `${API_PREFIX}/assessment/${id}/customquestiontime/update`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiUpdateAssessmentSettings = async (id: string, body: object) => {
        try {
            return await axios.put(
                `${API_PREFIX}/assessment/${id}/setting`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetAssessmentList = async (
        limit: number,
        skip: number,
        query: string,
        colName: string,
        inOrder: string,
        isArchived: boolean,
        isActive: boolean,
        isDraft: boolean,
        isEditable: boolean,
        workspaceLabelTitle: Array<string>,
        groupName: Array<string>,
        questionType: Array<string>,
        testLibraryId: Array<string>,
        startDate: string,
        endDate: string
    ) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment?limit=${limit}&skip=${skip}&query=${encodeURIComponent(
                    query
                )}&colName=${colName}&inOrder=${inOrder}&isArchived=${isArchived}&isEditable=${isEditable}&isActive=${isActive}&isDraft=${isDraft}&workspaceLabelTitle=${workspaceLabelTitle}&groupName=${groupName}&questionType=${questionType}&testLibraryId=${testLibraryId}&from=${startDate}&to=${endDate}`,
                {
                    headers: {
                        ...getApiRequestHeader.value.headers,
                        Referrer: window.location.href,
                    },
                }
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetAssessmentFeedback = async (
        ratings: number,
        review: string,
        assessmentId: string
    ) => {
        try {
            const body = {
                ratings,
                review,
                assessmentId,
            }
            return await axios.post(
                `${API_PREFIX}/assessment/feedback`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiInviteAssessmentCandidate = async (
        candidateInvites: Array<object>,
        assessmentId: string
    ) => {
        try {
            const body = {
                candidateInvites,
                assessmentId,
            }
            return await axios.post(
                `${API_PREFIX}/assessment/candidate/invites`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiArchiveAssessmentCandidate = async (
        assessmentCandidateId: string,
        isArchived: boolean
    ) => {
        try {
            const body = {
                isArchived,
            }
            return await axios.patch(
                `${API_PREFIX}/assessment/candidate/${assessmentCandidateId}/archive`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiArchiveBulkAssessmentCandidate = async (
        assessmentCandidateIds: string[],
        isArchived: boolean
    ) => {
        try {
            const body = {
                isArchived,
                assessmentCandidateIds,
            }
            return await axios.patch(
                `${API_PREFIX}/assessment/candidate/bulk/archive`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiInviteInterviewCandidate = async (
        candidateGroupId: string,
        jobRole: string
    ) => {
        try {
            const body = {
                candidateGroupId,
                jobRole,
            }
            return await axios.post(
                `${API_PREFIX}/interview/candidate/invite`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetTalentList = async (
        limit: number,
        skip: number,
        skill: string,
        industry: string[],
        location: string[],
        query: string,
        jobRoleId: string,
        interviewStatus: string[]
    ) => {
        try {
            return await axios.get(
                `${API_PREFIX}/candidate/profile/list?limit=${limit}&skip=${skip}&skills=${skill}&industry=${industry}&query=${query}&location=${location}&jobRoleId=${jobRoleId}&interviewStatus=${interviewStatus}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetUnlockCandidateDetails = async (id: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/candidate/profile/contact/${id}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetAssessmentListForInterview = async (candidateId: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/candidate/${candidateId}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiReinviteAssessmentCandidate = async (
        candidateInvites: Array<object>,
        assessmentId: string
    ) => {
        try {
            const body = {
                candidateInvites,
                assessmentId,
            }
            const response: any = await axios.post(
                `${API_PREFIX}/assessment/candidate/invites`,
                body,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetAssessmentBasicDetails = async (id: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/${id}/basic/detail`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetAssessmentQuestionDetails = async (id: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/${id}/question/detail`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetAssessmentTestDetails = async (id: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/${id}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetAssessmentCandidateDetails = async (
        id: string,
        limit: number,
        skip: number,
        query: string,
        candidateStatus: string,
        candidateStage: string,
        grade: string,
        colName: string,
        inOrder: string,
        range: string,
        workspaceLabelTitle: Array<object>,
        invitedBy: string,
        invitationType: string,
        isToBeScored: string,
        visibility: string
    ) => {
        const visibilityFilter = visibility.length > 0 ? visibility : ["active"]
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/${id}/candidate?limit=${limit}&skip=${skip}&query=${encodeURIComponent(
                    query
                )}&candidateStatus=${candidateStatus}&candidateStage=${candidateStage}&grade=${grade}&colName=${colName}&inOrder=${inOrder}&completedRange=${range}&workspaceLabelTitle=${workspaceLabelTitle}&invitedBy=${invitedBy}&invitationType=${invitationType}&isToBeScored=${isToBeScored}&visibility=${visibilityFilter}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetAssessmentLists = async (id: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/${id}/assessments`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiDownloadFile = async (
        id: string,
        fileType: string,
        candidateIds: string[] = [],
        timeZone: string,
        options?: {
            downloadAll: boolean
            status?: string[]
            candidateStage?: string[]
            visibility?: string
        }
    ) => {
        try {
            const downloadAll = options?.downloadAll ?? false
            const body = {
                candidateIds: downloadAll ? [] : candidateIds,
                timeZone: timeZone,
                downloadAll: downloadAll || !candidateIds.length,
                status: options.status ?? [],
                candidateStage: options.candidateStage ?? [],
                visibility: options.visibility ?? "",
            }
            const response: any = await axios.post(
                `${API_PREFIX}/assessment/${id}/candidate/file/download/${fileType}/async`,
                body,
                {
                    headers: {
                        "Content-Type":
                            fileType === "csv"
                                ? "text/csv"
                                : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    },
                    ...getApiRequestHeader.value,
                }
            )
            return response
        } catch ({ response }) {
            handleError(response)
            return
        }
    }
    const apiDownloadLibraryCSV = async (id: string, type: string) => {
        try {
            const body = {
                id,
                type,
            }
            const response: any = await axios.post(
                `${API_PREFIX}/test/library/${id}/questions/download/${type}`,
                body,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            return response.data
        }
    }
    const apiDownloadPdfFile = async (
        assessmentId: string,
        candidateId: string,
        report: string = "",
        testLibId: string = "",
        type: string = "",
        isPublicLink: boolean = false,
        orgId: string = ""
    ) => {
        try {
            const authParam = route.query.hasOwnProperty("auth")
                ? route.query["auth"]
                : null

            let URL = ""
            let headers = {}

            if (authParam) {
                // If `auth` param exists in the URL query, use public auth URL
                URL = `${API_PREFIX}/public/assessment/candidate/report?fileType=pdf&testLibId=${testLibId}&auth=${authParam}`
                headers = getApiRequestHeader.value // or {} if no headers needed
            } else if (isPublicLink) {
                // Handle public link with predefined auth token
                URL = `${API_PREFIX}/public/assessment/${assessmentId}/candidate/${candidateId}/org/${orgId}`
                headers = PUBLIC_AUTH_HEADER
            } else {
                // Standard private link
                URL = `${API_PREFIX}/assessment/${assessmentId}/candidate/${candidateId}?fileType=pdf&report=${report}&testLibId=${testLibId}`
                headers = getApiRequestHeader.value
            }

            return await axios.get(URL, headers).then((response) => {
                let fileArr = []
                if (isPublicLink) response = response.data
                if (authParam) response.data = response
                const fileLink = response.data.pdfUrl
                const cefrLink = response.data.cefrCertificatePdfUrl
                const psychometricTestLink = response.data.psychometricTestPdf
                const customerServiceAdvisorLink =
                    response.data.customerServiceAdvisorTestPdf
                const conflictManagementLink =
                    response.data.conflictManagementSkillsTestPdf
                const workEtiquetteLink = response.data.workEtiquetteTestPdf
                const generalWorkforceLink =
                    response.data.generalWorkforceTestPdf
                let timestamp = new Date().getTime()
                let SJTResults = response.data.commonSJTReports
                    ? response.data.commonSJTReports
                    : []

                let firstName = response.data?.firstName
                let lastName = response.data.lastName
                let assessmentTitle = response.data.title

                if (report === "" || type == "sjt") {
                    if (SJTResults.length > 0 && SJTResults !== undefined) {
                        SJTResults?.forEach((item) => {
                            fileArr.push({
                                name:
                                    `${firstName} ${lastName} - ${item.title}` +
                                    ".pdf",
                                url: item.reportUrl,
                            })
                        })
                    }
                }

                if (testLibId !== "") {
                    const testlib = response.data.testLibrary.find(
                        (item) => item.testLibraryId === testLibId
                    )
                    const testLibraryTitle = testlib.testLibraryTitle
                    fileArr.push({
                        name:
                            response.data.firstName +
                            " " +
                            response.data.lastName +
                            " - " +
                            testLibraryTitle +
                            ".pdf",
                        url: testlib?.pdfReportUrl,
                    })
                }
                if (
                    report === "cefr" ||
                    (report === "" && cefrLink && type !== "sjt")
                ) {
                    fileArr.push({
                        name:
                            response.data.firstName +
                            " " +
                            response.data.lastName +
                            " - " +
                            assessmentTitle +
                            ".pdf",
                        url: cefrLink,
                    })
                }
                if (
                    report === "assessmentReport" ||
                    (report === "" && fileLink && type != "sjt")
                ) {
                    fileArr.push({
                        name:
                            response.data.firstName +
                            " " +
                            response.data.lastName +
                            " - " +
                            assessmentTitle +
                            ".pdf",
                        url: fileLink,
                    })
                }
                if (
                    report === "smart" ||
                    (report === "" && psychometricTestLink && type != "sjt")
                ) {
                    const testLibraryTitle = response.data.testLibrary.map(
                        (item: any) => {
                            return item.testLibraryTitle
                        }
                    )
                    let selectedTitle
                    if (
                        testLibraryTitle.includes(
                            "SMART - Testlify Personality Assessment (New)"
                        )
                    ) {
                        selectedTitle =
                            "SMART - Testlify Personality Assessment (New)"
                    }
                    fileArr.push({
                        name:
                            response.data.firstName +
                            " " +
                            response.data.lastName +
                            " - " +
                            selectedTitle +
                            ".pdf",
                        url: psychometricTestLink,
                    })
                }
                if (
                    report === "csa" ||
                    (report === "" &&
                        customerServiceAdvisorLink &&
                        type != "sjt")
                ) {
                    fileArr.push({
                        name:
                            response.data.firstName +
                            " " +
                            response.data.lastName +
                            " - " +
                            assessmentTitle +
                            ".pdf",
                        url: customerServiceAdvisorLink,
                    })
                }
                if (
                    report === "cms" ||
                    (report === "" && conflictManagementLink && type != "sjt")
                ) {
                    fileArr.push({
                        name:
                            response.data.firstName +
                            " " +
                            response.data.lastName +
                            " - " +
                            assessmentTitle +
                            ".pdf",
                        url: conflictManagementLink,
                    })
                }
                if (
                    report === "wet" ||
                    (report === "" && workEtiquetteLink && type != "sjt")
                ) {
                    fileArr.push({
                        name:
                            response.data.firstName +
                            " " +
                            response.data.lastName +
                            " - " +
                            assessmentTitle +
                            ".pdf",
                        url: workEtiquetteLink,
                    })
                }
                if (
                    report === "gwt" ||
                    (report === "" && generalWorkforceLink && type != "sjt")
                ) {
                    fileArr.push({
                        name:
                            response.data.firstName +
                            " " +
                            response.data.lastName +
                            " - " +
                            assessmentTitle +
                            ".pdf",
                        url: generalWorkforceLink,
                    })
                }
                if (report === "") {
                    const validFileArr = fileArr.filter(
                        (item) => item.url !== "null" && item.url !== null
                    )
                    downloadAll(validFileArr, response.data)
                } else {
                    downloadPdf(
                        fileArr[0].url + "?v=" + timestamp,
                        fileArr[0].name
                    )
                }
                return 1
            })
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const downloadPdf = async (link: string, pdfName: string) => {
        try {
            const newHostname = "testlify-eu.s3.amazonaws.com"
            const updatedUrl = link
                .replace("d5l8zhw44l206.cloudfront.net", newHostname)
                .replace("cdn-global.net", newHostname)
            const response = await axios.get(updatedUrl, {
                responseType: "arraybuffer",
            })
            const data = new Uint8Array(response.data)

            const blob = new Blob([data], { type: "application/pdf" })
            const url = URL.createObjectURL(blob)

            const a = document.createElement("a")
            a.href = url
            a.download = pdfName

            // Trigger the click event
            const clickEvent = new MouseEvent("click", {
                view: window,
                bubbles: true,
                cancelable: false,
            })
            a.dispatchEvent(clickEvent)

            URL.revokeObjectURL(url)
        } catch (error) {
            //bearer:disable javascript_lang_logger_leak
            console.error("Error downloading PDF:", error)
        }
    }

    const download = async (item) => {
        const newHostname = "testlify-eu.s3.amazonaws.com"
        const updatedUrl = item.url
            .replace("d5l8zhw44l206.cloudfront.net", newHostname)
            .replace("cdn-global.net", newHostname)
        let timestamp = new Date().getTime() // To get the timestamp
        //download single file as blob and add it to zip archive
        const resp = await axios.get(updatedUrl + "?v=" + timestamp, {
            responseType: "blob",
        })
        zip.file(item.name, resp.data)
    }

    const downloadAll = async (fileArr: any[], data: any) => {
        const arrOfFiles = fileArr.map((item: any) => download(item)) //create array of promises
        Promise.all(arrOfFiles)
            .then(() => {
                //when all promises resolved - save zip file
                zip.generateAsync({ type: "blob" }).then(function (blob) {
                    saveAs(
                        blob,
                        data.firstName +
                            " " +
                            data.lastName +
                            " - " +
                            data.assessmentName +
                            ".zip"
                    )
                })
            })
            .catch((err) => {
                //bearer:disable javascript_lang_logger_leak
                console.log(err)
            })
    }

    const apiSendResult = async (assessmentId: string, candidateId: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/${assessmentId}/candidate/${candidateId}/result/send`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiDeleteBulkCandidate = async (
        assessmentId: string,
        candidateIds: any,
        isAllSelect: boolean,
        query: string,
        candidateStatus: string,
        candidateStage: string,
        grade: string,
        range: string,
        workspaceLabelTitle: string,
        invitedBy: string,
        invitationType: string,
        isToBeScored: string
    ) => {
        try {
            const apiConfig = getApiRequestHeader.value
            const url = `${API_PREFIX}/assessment/${assessmentId}/candidate/bulk?query=${encodeURIComponent(
                query
            )}&candidateStatus=${candidateStatus}&candidateStage=${candidateStage}&grade=${grade}&completedRange=${range}&workspaceLabelTitle=${workspaceLabelTitle}&invitedBy=${invitedBy}&invitationType=${invitationType}&isToBeScored=${isToBeScored}`
            const data = {
                candidateIds,
                isAllSelect,
            }
            return await axios.delete(url, { ...apiConfig, data })
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiDeleteCustomQuestion = async (
        assessmentId: string,
        id: string
    ) => {
        try {
            return await axios.delete(
                `${API_PREFIX}/assessment/custom/question/${assessmentId}/${id}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiBulkInviteCandidates = async (
        assessmentId: string,
        csvFile: string
    ) => {
        try {
            const headers = {
                Authorization: getApiRequestHeader.value.headers.Authorization,
                "Content-Type": "multipart/form-data",
            }
            const formData = new FormData()
            formData.append("file", csvFile)
            return await axios.post(
                `${API_PREFIX}/assessment/${assessmentId}/candidate/bulk/invite/async`,
                formData,
                { headers }
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiJoinWaitlist = async (libraryId: string) => {
        try {
            const body = {
                id: libraryId,
            }
            return await axios.post(
                `${API_PREFIX}/test/library/${libraryId}/waitlist`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGenerateCandidateInvitationLink = async (
        assessmentId: string,
        email: string
    ) => {
        try {
            const body = { email }
            return await axios.post(
                `${API_PREFIX}/assessment/${assessmentId}/candidate/link`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            return response.data
        }
    }
    const apiGetCodingLanguages = async (query: string = "") => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/coding/language?query=${query}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetCodingTemplates = async (query: string = "") => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/coding/template?query=${query}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetCodingLanguageSnippetCode = async (
        codingLanguageId: string
    ) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/candidate/coding/question/snippet/${codingLanguageId}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            return response.data
        }
    }
    const apiCreateCodingQuestion = async (
        sourceId: string,
        title: string,
        timeLimit: number,
        difficultyLevel: string,
        question: string,
        codingLanguages: Array<object>,
        testCases: Array<object>,
        maximumScore: number
    ) => {
        try {
            const body = {
                sourceId,
                title,
                timeLimit,
                difficultyLevel,
                question,
                codingLanguages,
                testCases,
                maximumScore,
            }
            return await axios.post(
                `${API_PREFIX}/assessment/custom/question/coding/question`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetCodingQuestion = async (testLibraryId: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/custom/question/coding/question/${testLibraryId}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiUpdateCodingQuestion = async (
        testLibraryId: string,
        questionId: string,
        title: string,
        timeLimit: number,
        question: string,
        testCases: Array<object>,
        codingLanguages: Array<object>,
        difficultyLevel: string,
        maximumScore: number
    ) => {
        try {
            const body = {
                testLibraryId,
                questionId,
                title,
                timeLimit,
                question,
                testCases,
                codingLanguages,
                difficultyLevel,
                maximumScore,
            }
            return await axios.put(
                `${API_PREFIX}/assessment/custom/question/coding/question`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const CAND_PREFIX = "https://candidate.testlify.com/api/v1"
    const KSA_CAND_PREFIX = "https://candidate-ksa.testlify.com/api/v1"
    const USA_CAND_PREFIX = "https://candidate-usa.testlify.com/api/v1"
    const STAGING = "https://testlify-candidate-staging.vercel.app/api/v1"

    const SAUDI_ARABIA = "SAUDI_ARABIA" // assuming constant is defined elsewhere or pass it in

    const getCandidateApiUrl = (region?: string): string => {
        const href = window.location.href

        const isLocalOrStaging =
            href.includes("localhost") || href.includes("vercel")

        const isKSA =
            href.includes("tamayaz.sa") ||
            href.includes("albayanassessment.com") ||
            (!isLocalOrStaging && region?.includes(SAUDI_ARABIA))

        const isUSA = href.includes("usaapp.testlify.com")

        if (isKSA) return KSA_CAND_PREFIX
        if (isUSA) return USA_CAND_PREFIX
        return isLocalOrStaging ? STAGING : CAND_PREFIX
    }

    const apiUploadCandidateFile = async (
        orgId: string,
        assessmentId: string,
        candidateId: string,
        questionId: string,
        region?: string // add region as an optional argument
    ) => {
        const API_URL = getCandidateApiUrl(region)
        const body = { orgId, assessmentId, candidateId, questionId }

        try {
            return await axios.post(
                `${API_URL}/assessment/candidate/fileupload/${questionId}`,
                body,
                getApiRequestHeader.value
            )
        } catch (error: any) {
            handleError(error?.response)
            return -1
        }
    }

    const apiAssessmentQuestionSetScore = async (
        assessmentId: string,
        candidateId: string,
        questionId: string,
        score: number,
        criteriaScoring?: Array<object>
    ) => {
        try {
            const body = {
                assessmentId,
                candidateId,
                questionId,
                score,
                criteriaScoring,
            }
            const response = await axios.put(
                `${API_PREFIX}/assessment/custom/question/score`,
                body,
                getApiRequestHeader.value
            )
            if (response.status === 204)
                toast.success(
                    `${i18n.global.t(
                        "custom_que.toast_notifications.score_updated"
                    )}`
                )
            return response
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiAssessmentInsight = async (
        assessmentId: string,
        candidateId: string,
        regenerate: boolean,
        reaction: boolean,
        generateInsight: boolean,
        generateInterviewQuestions: boolean
    ) => {
        try {
            const body = {
                regenerate,
                reaction,
                generateInsight,
                generateInterviewQuestions,
            }
            const response = await axios.patch(
                `${API_PREFIX}/assessment/${assessmentId}/candidate/${candidateId}/insight`,
                body,
                getApiRequestHeader.value
            )
            if (response.status === 204)
                toast.success(
                    `${i18n.global.t(
                        "custom_que.toast_notifications.score_updated"
                    )}`
                )
            return response
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiAssessmentTestQuestionSetScore = async (
        assessmentId: string,
        candidateId: string,
        questionId: string,
        score: number,
        criteriaScoring?: Array<object>
    ) => {
        try {
            const body = {
                assessmentId,
                candidateId,
                questionId,
                score,
                criteriaScoring,
            }
            const response = await axios.put(
                `${API_PREFIX}/assessment/question/score`,
                body,
                getApiRequestHeader.value
            )
            if (response.status === 204)
                toast.success(
                    `${i18n.global.t(
                        "custom_que.toast_notifications.score_updated"
                    )}`
                )
            return response
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const getTestLibrariesId = async (
        lang: string,
        query?: string,
        libraryId?: string
    ) => {
        try {
            return await axios.get(
                `${API_PREFIX}/test/library/filter?language=${lang}&search=${query}&library=${libraryId}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiVerifyDocumentLink = async (docLink: string, docType: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/custom/question/document/verify/link?docLink=${docLink}&docType=${docType}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            toast.error(
                `${i18n.global.t("add_test_case_modal.necessary_permission")}`
            )
            return -1
        }
    }

    const apiGetAssessmentStatusCount = async (assessmentId: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/${assessmentId}/candidate/status`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetQuestionList = async (
        limit: number,
        skip: number,
        query: string,
        testLibraryId: string[],
        type: string[],
        difficulty: string[],
        isCompanyTest: boolean,
        isTestlifyLibraries: boolean,
        language: string[],
        isStarred: boolean,
        isCompany: boolean = false
    ) => {
        try {
            return await axios.get(
                `${API_PREFIX}/question?limit=${limit}&skip=${skip}&query=${encodeURIComponent(
                    query
                )}&type=${type}&difficulty=${difficulty}&testLibraryId=${testLibraryId}&isCompanyTestLibraries=${isCompanyTest}&isTestlifyLibraries=${isTestlifyLibraries}&language=${language}&isStarred=${isStarred}&isCompanyTest=${isCompany}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetNotificationList = async (
        assessmentId: string,
        limit: number,
        skip: number
    ) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/notifications?assessmentId=${assessmentId}&limit=${limit}&skip=${skip}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetNotificationAssessmentList = async () => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/filter/list`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiMarkAllReadNotifications = async (notificationIds: string[]) => {
        try {
            const body = { notificationIds: [notificationIds] }
            return await axios.patch(
                `${API_PREFIX}/assessment/notifications/read`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiStarMarkQuestion = async (questionId: string) => {
        try {
            const body = { questionId }
            return await axios.post(
                `${API_PREFIX}/question/starred/${questionId}`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiUnStarQuestion = async (questionId: string) => {
        try {
            return await axios.delete(
                `${API_PREFIX}/question/starred/${questionId}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetQuestion = async (questionId: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/question/${questionId}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetTestQuestions = async (
        assessmentId: string,
        candidateId: string,
        libraryId: string
    ) => {
        try {
            if (route.query.hasOwnProperty("auth")) {
                const auth = route.query["auth"]
                // use this url when shareable link is opened
                let response: any = await axios.get(
                    `${API_PREFIX}/public/assessment/candidate/library/${libraryId}?auth=${auth}`
                )
                return response
            } else {
                let response: any = await axios.get(
                    `${API_PREFIX}/assessment/${assessmentId}/candidate/${candidateId}/library/${libraryId}`,
                    getApiRequestHeader.value
                )
                return response
            }
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetAnalyticQuestionEmbed = async (
        assessmentId: string = "",
        fromDate: string = "",
        toDate: string = "",
        userId: string = ""
    ) => {
        try {
            return await axios.get(
                `${API_PREFIX}/analytics/question/embed?assessmentId=${
                    assessmentId ?? ""
                }&from_date=${fromDate}&to_date=${toDate}&userId=${
                    userId ?? ""
                } `,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiCorrectionRequest = async (
        assessmentId: any,
        candidateId: any,
        questionId: string,
        questionType: string,
        options: any[],
        comment: string
    ) => {
        try {
            let body

            if (questionId && questionType) {
                // If question is loaded then current question ID and Type will be sent
                // If question is not loaded then previous question ID and Type will be sent
                body = {
                    questionId,
                    questionType,
                    options,
                    comment,
                    isFirstQuestion: false,
                }
            } else {
                //If first question is not loaded
                body = {
                    options,
                    comment,
                    isFirstQuestion: false,
                }
            }

            const response: any = await axios.post(
                `${API_PREFIX}/assessment/${assessmentId}/candidate/${candidateId}/question/feedback`,
                body,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response?.data?.error)
            return -1
        }
    }
    const apiCopyAsCompanyTest = async (
        assessmentId: string,
        testLibraryTitle: string
    ) => {
        try {
            let body = { assessmentId, testLibraryTitle }
            return await axios.post(
                `${API_PREFIX}/assessment/${assessmentId}/questions/test`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiAssessmentPreview = async ({ firstName, lastName, email }) => {
        try {
            let body = {
                firstName,
                lastName,
                email,
            }
            return await axios.post(
                `${API_PREFIX}/assessment/preview`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiBulkScheduleInviteCandidates = async (
        assessmentId: string,
        csvFile: string,
        scheduledDate: string
    ) => {
        try {
            const headers = {
                Authorization: getApiRequestHeader.value.headers.Authorization,
                "Content-Type": "multipart/form-data",
            }
            const formData = new FormData()
            formData.append("file", csvFile)
            formData.append("scheduledDate", scheduledDate)
            return await axios.post(
                `${API_PREFIX}/assessment/${assessmentId}/candidate/bulk/schedule/invite`,
                formData,
                { headers }
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiScheduleInvite = async (
        candidateInvites: object = [],
        assessmentId: string | any,
        scheduledDate: string
    ) => {
        try {
            const body = {
                candidateInvites,
                assessmentId,
                scheduledDate,
            }
            return await axios.post(
                `${API_PREFIX}/assessment/candidate/schedule/invite`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetScheduleInvites = async (
        assessmentId: string | any,
        userIds: string[],
        limit: number,
        skip: number,
        query: string
    ) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/${assessmentId}/schedule/invite?q=${query}&skip=${skip}&limit=${limit}&userIds=${userIds}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiDeleteScheduleInvites = async (
        assessmentId: string,
        emails: object = [],
        isAllSelect: boolean = false
    ) => {
        try {
            const apiConfig = getApiRequestHeader.value
            const url = `${API_PREFIX}/assessment/${assessmentId}/schedule/invite`
            const data = {
                emails,
                isAllSelect,
            }
            return await axios.delete(url, { ...apiConfig, data })
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetAssessmentLabels = async (assessmentId: string | string[]) => {
        try {
            let response: any = await axios.get(
                `${API_PREFIX}/assessment/label/${assessmentId}`,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiDeleteAssessmentLabel = async (id: string, labelId: string) => {
        try {
            return await axios.delete(
                `${API_PREFIX}/assessment/label/${id}/label/${labelId}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiGetTestQuestionLevelConfig = async (id: string) => {
        try {
            return await axios.get(
                `${API_PREFIX}/test/library/${id}/questions/config`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiPutAssessmentConfig = async (
        id: string,
        questionCountType: string,
        config: any,
        setQuestionCount: number
    ) => {
        const body: any = {
            ...(config && { config }),
            ...(questionCountType && { questionCountType }),
            ...(questionCountType === "CUSTOM" &&
                setQuestionCount && { setQuestionCount }),
        }

        try {
            return await axios.put(
                `${API_PREFIX}/test/library/${id}/questions/config`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiCopyAssessmentSettings = async (
        sourceAssessmentId: string,
        targetAssessmentIds: [string]
    ) => {
        try {
            return await axios.post(
                `${API_PREFIX}/assessment/copy/settings?sourceAssessmentId=${sourceAssessmentId}&targetAssessmentIds=${targetAssessmentIds}`,
                {},
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiDeleteTalent = async (candidateId: string) => {
        try {
            return await axios.delete(
                `${API_PREFIX}/candidate/profile/${candidateId}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetVideoAiAssets = async () => {
        try {
            const response: any = await axios.get(
                `${API_PREFIX}/static/videoai/assets`,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiAssessmentComments = async (
        comment: string,
        assessmentCandidateId: string,
        candidateId: string
    ) => {
        const body = {
            comment,
            assessmentCandidateId,
            candidateId,
        }
        try {
            return await axios.post(
                `${API_PREFIX}/assessment/candidate/comments`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiUpdateAssessmentComments = async (id: string, comment: string) => {
        try {
            const body = {
                comment,
            }
            return await axios.put(
                `${API_PREFIX}/assessment/comment/${id}`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetComments = async (id: string) => {
        try {
            const response: any = await axios.get(
                `${API_PREFIX}/assessment/${id}/comments`,
                getApiRequestHeader.value
            )
            return response.data
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiDeleteComment = async (id: string) => {
        try {
            return await axios.delete(
                `${API_PREFIX}/assessment/comment/${id}`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiGetCommentList = async (
        id: string,
        limit: number,
        skip: number,
        inOrder: string
    ) => {
        try {
            return await axios.get(
                `${API_PREFIX}/assessment/${id}/comments?limit=${limit}&skip=${skip}
                    &inOrder=${inOrder}`,
                {
                    headers: {
                        ...getApiRequestHeader.value.headers,
                    },
                }
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiRecalculateAIScore = async (
        assessmentId: string = "",
        candidateId: string = "",
        questionId: string = ""
    ) => {
        if (!assessmentId || !candidateId) {
            return
        }

        try {
            const body = {
                assessmentId,
                candidateId,
                questionId,
            }
            await axios.post(
                `${API_PREFIX}/assessment/${assessmentId}/candidate/${candidateId}/recalculate?questionIds=${questionId}`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return
        }
    }
    const apiDeleteSnapshots = async (
        assessmentId: string,
        candidateId: string
    ) => {
        try {
            return await axios.delete(
                `${API_PREFIX}/assessment/${assessmentId}/candidate/${candidateId}/snapshots/delete`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }
    const apiDeleteCandidateProfile = async (
        assessmentId: string,
        candidateId: string
    ) => {
        try {
            return await axios.delete(
                `${API_PREFIX}/assessment/${assessmentId}/candidate/${candidateId}/profile/delete`,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    const apiSendTestCertificate = async (
        courseTitle: string,
        certificateTemplateId: string
    ) => {
        if (!courseTitle || !certificateTemplateId) {
            return -1
        }

        try {
            const body = {
                courseTitle,
                certificateTemplateId,
            }
            return await axios.post(
                `${API_PREFIX}/assessment/certificate/email`,
                body,
                getApiRequestHeader.value
            )
        } catch ({ response }) {
            handleError(response)
            return -1
        }
    }

    return {
        apiAssessmentPreview,
        apiAiAssessmentStatus,
        apiGetNotificationList,
        apiGetNotificationAssessmentList,
        apiMarkAllReadNotifications,
        apiGetJobRoleList,
        apiFetchSkillsGenerativeAI,
        apiGetTestList,
        apiGetTemplates,
        apiCreateAssessment,
        apiGetAssessmentLimit,
        apiAssessmentAnalytics,
        apiGetCompanyTestLimit,
        apiCreateAssessmentGenerativeAI,
        apiEditAssessment,
        apiGetTypeList,
        apiGetIndustryTypes,
        apiUpdateAssessment,
        apiGetAssessmentList,
        apiGetAssessmentFeedback,
        apiInviteAssessmentCandidate,
        apiInviteInterviewCandidate,
        apiGetTalentList,
        apiGetAssessmentListForInterview,
        apiReinviteAssessmentCandidate,
        apiGetAssessmentTestDetails,
        apiGetAssessmentCandidateDetails,
        apiDeleteCustomQuestion,
        apiBulkInviteCandidates,
        apiJoinWaitlist,
        apiDownloadFile,
        apiDownloadPdfFile,
        apiGenerateCandidateInvitationLink,
        apiEditArchive,
        apiArchiveAssessment,
        apiDeleteAssessment,
        apiSendResult,
        apiDeleteBulkCandidate,
        apiGetCodingLanguages,
        apiGetCodingTemplates,
        apiGetCodingLanguageSnippetCode,
        apiCloneAssessment,
        apiCloneCompanyTest,
        apiUpdateCodingQuestion,
        apiUploadCandidateFile,
        apiGetCodingQuestion,
        apiCreateCodingQuestion,
        apiSessionRecordings,
        apiUpdateAssessmentSettings,
        apiAssessmentQuestionSetScore,
        apiAssessmentInsight,
        apiAssessmentTestQuestionSetScore,
        apiVerifyDocumentLink,
        apiGetTestLangugesList,
        apiGetAssessmentLists,
        apiGetQuestionList,
        apiGetAssessmentStatusCount,
        getTestLibrariesId,
        apiStarMarkQuestion,
        apiUnStarQuestion,
        apiGetQuestion,
        apiUpdateTestLibrary,
        apiUpdateInstruction,
        apiSetTimeToCompleteQuestions,
        apiUpdateInstructionAssessment,
        apiGetTestQuestions,
        apiGetAnalyticQuestionEmbed,
        apiGetSkillTypes,
        apiGetLocationTypes,
        apiCorrectionRequest,
        apiGetUnlockCandidateDetails,
        apiCopyAsCompanyTest,
        apiGetInterviewStatusCount,
        apiBulkScheduleInviteCandidates,
        apiScheduleInvite,
        apiGetScheduleInvites,
        apiDeleteScheduleInvites,
        apiGetAssessmentLabels,
        apiDeleteAssessmentLabel,
        apiGetTestQuestionLevelConfig,
        apiGetAssessmentBasicDetails,
        apiGetAssessmentQuestionDetails,
        apiBulkDeleteAssessment,
        apiPutAssessmentConfig,
        apiCopyAssessmentSettings,
        apiDeleteTalent,
        apiAssessmentUsersAndRoles,
        apiGetVideoAiAssets,
        apiAssessmentComments,
        apiUpdateAssessmentComments,
        apiGetComments,
        apiDeleteComment,
        apiGetCommentList,
        apiAIWrite,
        apiGetAIAssitToken,
        apiRecalculateAIScore,
        apiDeleteSnapshots,
        apiDownloadLibraryCSV,
        apiArchiveAssessmentCandidate,
        apiArchiveBulkAssessmentCandidate,
        apiDeleteCandidateProfile,
        apiSendTestCertificate,
    }
}
